import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import Scrollbar from '../Scrollbar';
import { UserListHead } from '../user';
import { formatDate, wasAliveAtInstant } from '../../utils/billing';

// The "Deployments" card used on both the team-scoped and company-wide
// part-number views. Renders the header copy, the table, and the row
// formatting rules (VRack link when active, treatment chip). The company
// view passes `showTeamColumn` so each row also shows the originating team.
//
// `noCard` is used when the table is rendered inside another Card
// (e.g. as the second half of `DeploymentsPeakUsageAndTableCard`),
// where wrapping the table in its own Card would produce a nested
// Card and the page would visually double-up on the card chrome.
//
// `activeDay` (ISO 'YYYY-MM-DD') and `peakInstant` (epoch millis of
// the bar's peak) drive the row-highlight visual: when both are
// set, rows that were alive at `peakInstant` render with a
// green-tinted background so the user can see at a glance which
// racks contributed to the bar's peak. Racks that overlapped the
// day window but weren't alive at the peak instant are still shown
// (the table lists every rack active on the day for context) but
// stay unhighlighted so the contributing subset stands out.
DeploymentsTableCard.propTypes = {
  headLabel: PropTypes.array.isRequired,
  deployments: PropTypes.array,
  description: PropTypes.string,
  emptyMessage: PropTypes.string,
  showTeamColumn: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.oneOf(['asc', 'desc']),
  tableMinWidth: PropTypes.number,
  noCard: PropTypes.bool,
  activeDay: PropTypes.string,
  peakInstant: PropTypes.number
};

DeploymentsTableCard.defaultProps = {
  deployments: [],
  description: '',
  emptyMessage: 'No deployments recorded.',
  showTeamColumn: false,
  defaultOrderBy: 'vrack',
  defaultOrder: 'asc',
  tableMinWidth: 900,
  noCard: false,
  activeDay: null,
  peakInstant: null
};

// True when the row should be highlighted for `activeDay`. The
// highlight is driven by `peakInstant` -- the bar's peak instant --
// so the green-washed rows sum to the bar's height. Without an
// `activeDay` (no bar selected) no row highlights.
export function wasConcurrentOnDay(row, activeDay, peakInstant) {
  if (!activeDay) return false;
  if (!row) return false;
  if (peakInstant == null) return false;
  return wasAliveAtInstant(row.createdDate, row.deletedDate, peakInstant);
}

// Extract the comparable value for a given column id. `team` is the only
// column whose value lives at a different key (`teamName` instead of the
// matching id); everything else reads `row[id]` directly. Dates come back
// from the API as ISO strings, so lexicographic comparison gives the same
// order as a Date comparison.
function getSortValue(row, id) {
  if (id === 'team') return row.teamName;
  return row[id];
}

export default function DeploymentsTableCard({
  headLabel,
  deployments,
  description,
  emptyMessage,
  showTeamColumn,
  defaultOrderBy,
  defaultOrder,
  tableMinWidth,
  noCard,
  activeDay,
  peakInstant
}) {
  // useTheme() is needed so the row-highlight `sx` object can branch
  // on theme.palette.mode (light vs dark). The sx prop itself only
  // accepts an object here -- passing a function form to a regular
  // component is silently dropped, leaving the row unhighlighted.
  const theme = useTheme();
  // When `showTeamColumn` is true, insert a `team` entry after the first
  // column so the VRack column stays leftmost and Team sits beside it. The
  // body rows already render VRack first and Team second, so the header
  // order has to match.
  const effectiveHeadLabel = showTeamColumn
    ? [headLabel[0], { id: 'team', label: 'Team', alignRight: false }, ...headLabel.slice(1)]
    : headLabel;
  const totalColumns = effectiveHeadLabel.length;

  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState(defaultOrder);

  // Pick a highlight background that contrasts with the row's default
  // text in both themes:
  //   light: pale sky-blue (`primary.lighter`) on the off-white card
  //     background -- text default is GREY[800], reads cleanly.
  //   dark: `primary.lighter` is too close in luminance to the light
  //     row text (GREY[300]) so text looks washed out; `primary.darker`
  //     is too close to the table's #141C25 paper background to read
  //     as a highlight. Blend `primary.main` over the paper at 0.24
  //     opacity (the same opacity the theme uses for primary shadows)
  //     so the highlight is clearly visible but still dark enough for
  //     the light text to stay legible.
  // Memoized so the `sx` object reference is stable across renders
  // and MUI's class hashing doesn't churn on every parent update.
  const concurrentRowSx = useMemo(() => {
    const bg =
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.24)
        : theme.palette.primary.lighter;
    return {
      backgroundColor: bg,
      '&:hover': { backgroundColor: bg }
    };
  }, [theme]);

  const handleRequestSort = useCallback(
    (event, property) => {
      // MUI's convention: clicking the active column flips direction;
      // clicking a different column resets to ascending.
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const sortedDeployments = useMemo(() => {
    // Sort a shallow copy so we never mutate the caller's array — the
    // parent may pass the same `deployments` reference on re-renders and
    // expect it to stay in API order.
    const copy = [...deployments];
    copy.sort((a, b) => {
      const av = getSortValue(a, orderBy);
      const bv = getSortValue(b, orderBy);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return order === 'asc' ? -1 : 1;
      if (av > bv) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [deployments, orderBy, order]);

  // The header + table are the same in both layouts; only the outer
  // Card wrapper is conditional. `noCard` is used by the combined
  // chart+table view so we don't render nested cards.
  const body = (
    <>
      <Box sx={{ px: 3, pt: 2 }}>
        <Typography variant="h6">Deployments</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Scrollbar>
        <TableContainer sx={{ minWidth: tableMinWidth, mx: 1 }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={effectiveHeadLabel}
              rowCount={sortedDeployments.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedDeployments.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={totalColumns} sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedDeployments.map((row) => {
                  const isConcurrentOnActiveDay = wasConcurrentOnDay(row, activeDay, peakInstant);
                  return (
                    <TableRow
                      key={row.id}
                      data-testid={`deployment-row-${row.vrackId}`}
                      sx={isConcurrentOnActiveDay ? concurrentRowSx : undefined}
                    >
                      <TableCell align="left">
                        {row.deletedDate == null ? (
                          <Link
                            component={RouterLink}
                            to={`/racks/details/${encodeURIComponent(row.vrackId)}`}
                            underline="hover"
                            // Use the theme's primary color so the
                            // link reads correctly in both light and
                            // dark mode (a hardcoded hex would stay
                            // the same shade across themes and lose
                            // contrast against the table background
                            // in dark mode). `primary.main` is the
                            // brand-aligned shade in light mode and
                            // the lighter variant in dark mode --
                            // either way it stays legible.
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          >
                            {row.vrackId}
                          </Link>
                        ) : (
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {row.vrackId}
                          </Box>
                        )}
                      </TableCell>
                      {showTeamColumn && <TableCell align="left">{row.teamName}</TableCell>}
                      <TableCell align="left">
                        <strong>{row.quantity}</strong>
                      </TableCell>
                      <TableCell align="left">{row.owner}</TableCell>
                      <TableCell align="left">{formatDate(row.createdDate)}</TableCell>
                      <TableCell align="left">{formatDate(row.deletedDate)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </>
  );

  if (noCard) return body;
  return <Card>{body}</Card>;
}
